import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAdvanceToUnansweredQuestion, useNavigation } from '../../Hooks';
import { prepareFormTransitionContainerForCustomExitAnimation } from '../Transition/transition.helpers';
import {
  setBlockedDestinationPage,
  setShouldNavigateBackward,
  setShouldNavigateForward,
  setAttemptToNavigate,
} from '../../Lib/redux/slices/navigationControls';
import { isNavigationBlockedOnPage } from '../../Hooks/useNavigation/useNavigation.helpers';
import SubmissionStatus from '../../Data/SubmissionStatus';
import PreloadStatus from '../../Data/PreloadStatus';
import { setIsLoading } from '../../Lib/redux/slices/navigation';
import { filterComponentsForPage } from '../../Helpers/components';

const Navigation = () => {
  const dispatch = useDispatch();
  const { navigateForward, navigateBackward } = useNavigation();
  const {
    shouldNavigateForward,
    shouldNavigateBackward,
    pendingMoveBackwards,
    pendingMoveForwards,
    blockedDestinationPage,
    pagesWithNavigationBlocks,
    attemptToNavigate,
  } = useSelector((store) => store.navigationControls);
  const isBounceNavigationAnimationEnabled = useSelector(
    (store) => store.features.isBounceNavigationAnimationEnabled
  );
  const { isTransitionAnimationEnabledForSubmission } = useSelector(
    (store) => store.transition
  );
  const { currentPage, totalPages } = useSelector((store) => store.navigation);
  const isPenultimatePage = currentPage + 1 === totalPages;
  const theme = useTheme();
  let { componentIdToSagaStatus } = useSelector((store) => store.dataMappings);
  const { preloadStatus: appointmentBookingPreloadStatus } = useSelector(
    (store) => store.appointmentBooking
  );
  const { preloadStatus: cpcCardPreloadStatus } = useSelector(
    (store) => store.cpcCard
  );
  const { submissionStatus } = useSelector((store) => store.submission);
  const { isDynamicHeightEnabled, widgetResizeDuration } = useSelector(
    (store) => store.optionalFormConfig
  );
  const { isLegalStatementRevealed } = useSelector(
    (store) => store.legalStatement
  );
  const { advancePageStatus } = useSelector(
    (store) => store.sessionDataPopulation
  );
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);

  const pageComponentIds = pages[currentPage];
  const componentsOnPage = filterComponentsForPage(
    components,
    pageComponentIds
  );

  const isDestinationPageBlocked = !!(
    blockedDestinationPage &&
    isNavigationBlockedOnPage(blockedDestinationPage, pagesWithNavigationBlocks)
  );

  const isCpcCardLoading =
    cpcCardPreloadStatus === PreloadStatus.IN_PROGRESS ||
    cpcCardPreloadStatus === PreloadStatus.WAITING_FOR_START;

  const isAppointmentBookingLoading =
    appointmentBookingPreloadStatus === PreloadStatus.IN_PROGRESS ||
    appointmentBookingPreloadStatus === PreloadStatus.WAITING_FOR_START;

  if (!componentIdToSagaStatus) {
    componentIdToSagaStatus = {};
  }

  /**
   * isAnyAsyncOperationInProgress handles when the saga is in progress and it's return and update to store will interfere with the rendering of the next page.
   */
  const [isAnyAsyncOperationInProgress, setIsAnyAsyncOperationInProgress] =
    useState(
      Object.values(componentIdToSagaStatus).some(
        (status) => status === 'PENDING'
      )
    );

  const isSagaInProgress = Object.values(componentIdToSagaStatus).some(
    (status) => status === 'PENDING'
  );

  useEffect(() => {
    /**
     * componentIdToSagaStatus exists to prevent a race condition, we use the status to determine if the saga is in progress and if so we prevent the user from navigating to the next page
     */
    setIsAnyAsyncOperationInProgress(isSagaInProgress);
  }, [componentIdToSagaStatus]);

  const handlePreSubmissionSideEffect = () => {
    if (isTransitionAnimationEnabledForSubmission) {
      // Set up styles for a custom exit transition animation of the submission page
      prepareFormTransitionContainerForCustomExitAnimation({
        theme,
        widgetResizeDuration,
        isBounceNavigationAnimationEnabled,
      });
    }
  };

  useEffect(() => {
    if (shouldNavigateForward) {
      const isSubmissionInitiated = isPenultimatePage;
      if (isSubmissionInitiated) {
        handlePreSubmissionSideEffect();
      }
      navigateForward();
    }
  }, [shouldNavigateForward]);

  useEffect(() => {
    if (shouldNavigateBackward) {
      navigateBackward();
    }
  }, [shouldNavigateBackward]);

  useAdvanceToUnansweredQuestion({
    advancePageStatus,
    componentsOnPage,
    currentPage,
  });

  /**
   * pendingMoveForwards handles click forward and enter press
   */
  useEffect(() => {
    const isReadyForForwardNavigation =
      pendingMoveForwards === true &&
      isAnyAsyncOperationInProgress === false &&
      (cpcCardPreloadStatus === PreloadStatus.COMPLETED ||
        cpcCardPreloadStatus === PreloadStatus.NOT_STARTED) &&
      (submissionStatus === SubmissionStatus.COMPLETE ||
        submissionStatus === SubmissionStatus.NOT_STARTED) &&
      !isDestinationPageBlocked &&
      (appointmentBookingPreloadStatus === PreloadStatus.COMPLETED ||
        appointmentBookingPreloadStatus === PreloadStatus.NOT_STARTED);

    if (isReadyForForwardNavigation) {
      /*
       * If a dynamic height related temporary navigation block is in play, the
       * re-initiation of the nav instruction is managed by dynamic height
       * itself outside of this useEffect (and NOT by the 200ms-timeout callback
       * below)
       */
      const { pendingNavigationInstruction, blockNavigation } =
        window?.__private__?.dynamicWidgetHeightData || {};
      // artificial delay to allow the loading spinner to be displayed
      setTimeout(() => {
        const { isLegalStatementMountingOrUnmounting } =
          window?.__private__?.dynamicWidgetHeightData || {};

        const willNavigationBeReinitiatedByDynamicHeight =
          /*
           * If these two constants were truthy prior to the declaration of this
           * timeout (i.e. when the useEffect ran, just when pendingMoveForwards
           * had become true), then a dynamic height related temporary
           * navigation block is in play
           */
          !!(blockNavigation && pendingNavigationInstruction);

        if (blockedDestinationPage) {
          dispatch(
            setBlockedDestinationPage({
              pageNumber: null,
            })
          );
        }

        if (!isDynamicHeightEnabled || !isLegalStatementMountingOrUnmounting) {
          dispatch(setIsLoading({ isLoading: false }));
          if (!willNavigationBeReinitiatedByDynamicHeight) {
            dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
          }
        }
      }, 200);
    }

    if (
      pendingMoveForwards === false &&
      isAnyAsyncOperationInProgress === false &&
      (submissionStatus === SubmissionStatus.COMPLETE ||
        submissionStatus === SubmissionStatus.NOT_STARTED) &&
      (cpcCardPreloadStatus === PreloadStatus.COMPLETED ||
        cpcCardPreloadStatus === PreloadStatus.NOT_STARTED) &&
      !isDestinationPageBlocked &&
      (appointmentBookingPreloadStatus === PreloadStatus.COMPLETED ||
        appointmentBookingPreloadStatus === PreloadStatus.NOT_STARTED)
    ) {
      // Navigation has finished or cancelled, stop displaying a loading spinner
      dispatch(setIsLoading({ isLoading: false }));
    }

    if (
      pendingMoveForwards === true &&
      (isAnyAsyncOperationInProgress === true ||
        isCpcCardLoading === true ||
        submissionStatus === SubmissionStatus.IN_PROGRESS ||
        isAppointmentBookingLoading === true ||
        (isDynamicHeightEnabled && !isLegalStatementRevealed) ||
        isDestinationPageBlocked)
    ) {
      dispatch(setIsLoading({ isLoading: true }));
    }
  }, [
    pendingMoveForwards,
    isAnyAsyncOperationInProgress,
    cpcCardPreloadStatus,
    isDestinationPageBlocked,
    appointmentBookingPreloadStatus,
    submissionStatus,
  ]);

  useEffect(() => {
    if (
      pendingMoveBackwards === true &&
      isAnyAsyncOperationInProgress === false &&
      (appointmentBookingPreloadStatus === PreloadStatus.COMPLETED ||
        appointmentBookingPreloadStatus === PreloadStatus.NOT_STARTED)
    ) {
      dispatch(setShouldNavigateBackward({ shouldNavigateBackward: true }));
    }

    if (
      pendingMoveBackwards === false &&
      isAnyAsyncOperationInProgress === false &&
      (submissionStatus === SubmissionStatus.COMPLETE ||
        submissionStatus === SubmissionStatus.NOT_STARTED) &&
      (appointmentBookingPreloadStatus === PreloadStatus.COMPLETED ||
        appointmentBookingPreloadStatus === PreloadStatus.NOT_STARTED)
    ) {
      // Navigation has finished or cancelled, stop displaying a loading spinner
      dispatch(setIsLoading({ isLoading: false }));
    }

    if (
      pendingMoveBackwards === true &&
      (isAnyAsyncOperationInProgress === true ||
        isAppointmentBookingLoading === true)
    ) {
      dispatch(setIsLoading({ isLoading: true }));
    }
  }, [
    pendingMoveBackwards,
    isAnyAsyncOperationInProgress,
    appointmentBookingPreloadStatus,
  ]);

  useEffect(() => {
    if (attemptToNavigate && !isAnyAsyncOperationInProgress) {
      dispatch(setAttemptToNavigate({ attemptToNavigate: false }));
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [attemptToNavigate, isAnyAsyncOperationInProgress]);

  return null;
};

export default Navigation;
